import { useState, useEffect, useReducer, useRef, useLayoutEffect, } from 'react';
import { BehaviorSubject } from 'rxjs';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { shallowEqual } from '@famly/stat_ts-utils_shallow-equal';
const isPrimitive = (prop) => typeof prop === 'string' ||
    typeof prop === 'number' ||
    typeof prop === 'boolean' ||
    prop === undefined ||
    prop === null;
/**
 * Create a reducer that child components can subscribe to with a selector.
 * Subscribed components will only update when the return value
 * of the selector changes.
 *
 * @param reducer The reducer function to use
 * @returns Object with `useSubscribableReducer` and `useSubscription` hooks
 *
 * @example
 * const {useSubscribableReducer, useSubscription} = createSubscribableReducer(myReducer, emptyState);
 *
 * RootComponent() {
 *  const [state, dispatch] = useSubscribableReducer();
 *  return (
 *    <ChildComponent />
 *  )
 * }
 *
 * ChildComponent() {
 *   const myValue = useSubscription(state => state.foo.bar.biz)
 *
 *   return ...
 * }
 */
export const createSubscribableReducer = (reducer) => {
    let subject;
    const getSubject = () => {
        if (!hasValue(subject)) {
            throw new Error('Subscriber mounted with no mounted reducer');
        }
        return subject;
    };
    const subscribe = (listener) => {
        const subscription = getSubject().subscribe(listener);
        return () => subscription.unsubscribe();
    };
    const callSubscriptions = (state) => getSubject().next(state);
    const useSubscribableReducer = (initialState) => {
        if (!hasValue(subject)) {
            subject = new BehaviorSubject(initialState);
        }
        const [state, dispatch] = useReducer(reducer, initialState);
        // Notify subscribers at commit time (layout), not from a deferred passive effect.
        // A subscriber may feed the state into a controlled input's `value`; if that update
        // lands in a later, disconnected commit, React rewrites the DOM value and the browser
        // moves the caret to the end on every keystroke (FBS-11836). useLayoutEffect keeps the
        // notification synchronous with the dispatch that caused it.
        useLayoutEffect(() => {
            // Avoid throwing when there is no subject
            if (subject) {
                callSubscriptions(state);
            }
        }, [state]);
        useEffect(() => () => {
            // Remove subject when unmounting
            subject = undefined;
        }, []);
        return [state, dispatch];
    };
    function useSubscription(selector, computer) {
        /**
         * Keeping a reference to transformation functions so we only update
         * when store emits an update. This means that changes to your transformation
         * functions will _not_ (!!) trigger a new render.
         */
        const selectorRef = useRef(selector);
        const computerRef = useRef(computer);
        /**
         * By keeping a reference to the return value of selector we
         * can determine if the value needs to be computed again by comparing
         * the value of selected
         */
        const selected = useRef(selector(getSubject().value));
        const [computed, setComputed] = useState(hasValue(computer) ? computer(selected.current) : selected.current);
        useLayoutEffect(() => subscribe(state => {
            const newSelected = selectorRef.current(state);
            // Only compute new values when the selected state changes
            if (!isEqual(selected.current, newSelected)) {
                selected.current = newSelected;
                setComputed(hasValue(computerRef.current) ? computerRef.current(selected.current) : selected.current);
            }
        }), []);
        return computed;
    }
    /**
     * Reads the selected value from the store once, at mount, WITHOUT subscribing.
     *
     * Unlike `useSubscription`, this never re-renders the component when the selected
     * value changes afterwards. Use it to seed local component state from the store
     * when the component should own the value from then on — for example a controlled
     * input whose per-keystroke store round-trip would otherwise cascade into a React 19
     * "Maximum update depth exceeded" loop (see the observation editor reflection note).
     *
     * @param selector Reads the initial value from the current store state.
     * @returns The selected value captured at mount.
     */
    function useInitialValue(selector) {
        return useRef(selector(getSubject().value)).current;
    }
    return { useSubscribableReducer, useSubscription, useInitialValue };
};
const isEqual = (a, b) => {
    // If values are simple we can compare them with ===
    if (isPrimitive(a) || isPrimitive(b)) {
        return a === b;
    }
    // If values are objects we can use shallow-equal
    if (typeof a === 'object' && typeof b === 'object') {
        return shallowEqual(a, b);
    }
    // Otherwise we fall back to ===
    return a === b;
};
